/**
 * CLS logger - observes layout shifts and logs metrics for debugging
 * @module lib/clsLogger
 */

const DEFAULT_OPTIONS = {
  enabled: false,
  logEachShift: true,
  minShiftValue: 0.001,
  maxEntries: 50,
  logSummaryOnHide: true
};

function buildNodeSelector(node) {
  if (!node || !node.tagName) return null;
  let selector = node.tagName.toLowerCase();
  if (node.id) {
    selector += `#${node.id}`;
  }
  if (node.classList && node.classList.length) {
    const classes = Array.from(node.classList).slice(0, 3);
    if (classes.length) {
      selector += `.${classes.join('.')}`;
    }
  }
  return selector;
}

let initialized = false;

function initClsLogger(options = {}) {
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') {
    return { enabled: false, reason: 'unsupported' };
  }
  if (initialized) {
    return window.__millaCls || { enabled: true };
  }

  const enabled = options.enabled === true || window._millaLogCls === true;
  if (!enabled) {
    return { enabled: false, reason: 'disabled' };
  }

  const config = {
    ...DEFAULT_OPTIONS,
    ...options,
    enabled: true
  };

  const state = {
    enabled: true,
    totalShiftValue: 0,
    maxSessionValue: 0,
    sessionValue: 0,
    entries: []
  };

  const log = (...args) => {
    if (window.console?.info) {
      
    }
  };

  let firstSessionEntryTime = 0;
  let lastSessionEntryTime = 0;

  function recordEntry(entry) {
    const startTime = Number(entry.startTime || 0);
    if (
      firstSessionEntryTime === 0 ||
      startTime - lastSessionEntryTime > 1000 ||
      startTime - firstSessionEntryTime > 5000
    ) {
      firstSessionEntryTime = startTime;
      state.sessionValue = 0;
    }

    state.sessionValue += entry.value;
    lastSessionEntryTime = startTime;

    if (state.sessionValue > state.maxSessionValue) {
      state.maxSessionValue = state.sessionValue;
    }

    state.totalShiftValue += entry.value;
  }

  function maybeLogEntry(entry) {
    if (!config.logEachShift) return;
    if (entry.value < config.minShiftValue) return;

    const sources = Array.isArray(entry.sources)
      ? entry.sources
        .map((source) => buildNodeSelector(source && source.node))
        .filter(Boolean)
      : [];

    log('shift', {
      value: entry.value,
      sessionValue: state.sessionValue,
      maxSessionValue: state.maxSessionValue,
      startTime: Math.round(entry.startTime || 0),
      sources
    });
  }

  function logSummary(reason) {
    log('summary', {
      reason,
      totalShiftValue: Number(state.totalShiftValue.toFixed(4)),
      maxSessionValue: Number(state.maxSessionValue.toFixed(4)),
      entries: state.entries.length
    });
  }

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    entries.forEach((entry) => {
      if (entry.hadRecentInput) return;
      recordEntry(entry);
      state.entries.push(entry);
      if (state.entries.length > config.maxEntries) {
        state.entries.shift();
      }
      maybeLogEntry(entry);
    });
  });

  try {
    observer.observe({ type: 'layout-shift', buffered: true });
  } catch (err) {
    return { enabled: false, reason: 'observe_failed' };
  }

  if (config.logSummaryOnHide) {
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        logSummary('visibilitychange:hidden');
      }
    });
    window.addEventListener('pagehide', () => logSummary('pagehide'));
  }

  state.getSummary = () => ({
    totalShiftValue: Number(state.totalShiftValue.toFixed(4)),
    maxSessionValue: Number(state.maxSessionValue.toFixed(4)),
    entries: state.entries.length
  });

  window.__millaCls = state;
  initialized = true;
  return state;
}

module.exports = { initClsLogger };
